import { EditTwoTone, PlusOutlined, EyeFilled, LoadingOutlined } from "@ant-design/icons";
import {
  Table,
  Select,
  SelectProps,
  Spin,
} from "antd";
import React from "react";
import CTConfiguration from "../../Services/CTConfigurationService";
import Link from "antd/es/typography/Link";
import AddTaxRates from "./AddTaxRates";
import CommonService from "../../Services/CommonService";
import { useUser } from "../../Context/UserContext";
import { ColumnsType } from "antd/es/table";
import { TableToolbar } from "../../Common/Layout";

interface CTConfigurationTaxRate {
  year: number;
  smallProfitRate: number;
  mainRate: number;
  specialRate: number;
  ringFanceSmallProfitRate: number;
  ringFanceRate: number;
  mrUpperLimit: number;
  mrLowerLimit: number;
  standardFractionUp: number;
  standardFractionLow: number;
  ringFanceFractionUp: number;
  ringFanceFractionLow: number;
}

const TaxRates = () => {
  const [loading, setLoading] = React.useState(false);
  const [allData, setAllData] = React.useState<CTConfigurationTaxRate[]>([]);
  const [selectedYears, setSelectedYears] = React.useState<number[]>([]);
  const [yearOptions, setYearOptions] = React.useState<SelectProps["options"]>([]);
  const [addedYears, setAddedYears] = React.useState<number[]>([]);
  const [dialog, setDialog] = React.useState<any>({ type: "", params: {} });
  const { isSubAdmin } = useUser();

  const [tableParams, setTableParams] = React.useState({
    pagination: { current: 1, pageSize: 15, total: 0 },
    sorter: { field: "year", order: "descend" as any },
  });

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await CTConfiguration.getTaxRates();
      if (response.status && response.result) {
        setAllData(response.result);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const getYears = async () => {
      try {
        const res = await CommonService.getTaxRatesAddedYear();
        if (res.result && Array.isArray(res.result)) {
          setAddedYears(res.result);
          setYearOptions(
            res.result.map((year: number) => ({
              value: year,
              label: String(year),
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching years:", error);
      }
    };
    getYears();
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processedData = React.useMemo(() => {
    let data = [...allData];

    if (selectedYears.length > 0) {
      data = data.filter((item) => selectedYears.includes(item.year));
    }



    if (tableParams.sorter.field) {
      data.sort((a: any, b: any) => {
        const aVal = a[tableParams.sorter.field];
        const bVal = b[tableParams.sorter.field];

        if (typeof aVal === "number" && typeof bVal === "number") {
          return tableParams.sorter.order === "descend" ? bVal - aVal : aVal - bVal;
        }

        const aStr = String(aVal);
        const bStr = String(bVal);
        return tableParams.sorter.order === "descend"
          ? bStr.localeCompare(aStr)
          : aStr.localeCompare(bStr);
      });
    }

    const start = (tableParams.pagination.current - 1) * tableParams.pagination.pageSize;
    const end = start + tableParams.pagination.pageSize;
    const paginatedData = data.slice(start, end);

    return {
      data: paginatedData,
      total: data.length,
    };
  }, [allData, selectedYears, tableParams]);

  const columns: ColumnsType<CTConfigurationTaxRate> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: "4%",
      render: (_: any, _record: any, index: any) => (
        (tableParams.pagination.current - 1) * tableParams.pagination.pageSize + index + 1
      ),
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      width: "8%",
      sorter: true,
      showSorterTooltip: false,

    },
    {
      title: "Main rate",
      dataIndex: "mainRate",
      key: "mainRate",
      width: "10%",
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: "Special rate",
      dataIndex: "specialRate",
      key: "specialRate",
      width: "10%",
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: "Small profit rate",
      dataIndex: "smallProfitRate",
      key: "smallProfitRate",
      width: "10%",
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: "MR lower limit",
      dataIndex: "mrLowerLimit",
      key: "mrLowerLimit",
      width: "10%",
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: "MR upper limit",
      dataIndex: "mrUpperLimit",
      key: "mrUpperLimit",
      width: "10%",
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: "Ring fence rate",
      dataIndex: "ringFanceRate",
      key: "ringFanceRate",
      width: "10%",
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: "Standard fraction low",
      dataIndex: "standardFractionLow",
      key: "standardFractionLow",
      width: "12%",
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: "Standard fraction up",
      dataIndex: "standardFractionUp",
      key: "standardFractionUp",
      width: "12%",
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "5%",
      render: (_: any, record: any) => (
        <Link
          className="edit-btn"
          onClick={() => setDialog({ type: "addtaxrates", params: { ...record } })}
        >
          {isSubAdmin() ? <EyeFilled /> : <EditTwoTone />}
        </Link>
      ),
    },
  ];

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setTableParams({
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: processedData.total,
      },
      sorter: {
        field: sorter.field || "year",
        order: sorter.order || "descend",
      },
    });
  };

  const handleYearChange = (value: number[]) => {
    setSelectedYears(value);
    setTableParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, current: 1 },
    }));
  };

  const primaryActions: any[] = [];
  if (!isSubAdmin()) {
    primaryActions.push({
      label: "Tax Rate",
      icon: <PlusOutlined />,
      type: "primary",
      onClick: () => setDialog({ type: "addtaxrates", params: {} }),
    });
  }

  return (
    <div>
      <div>
        <TableToolbar
          primaryActions={primaryActions}
          extra={
            <Select
              mode="multiple"
              placeholder="Select Year"
              style={{ minWidth: 150 }}
              maxTagCount="responsive"
              options={yearOptions}
              value={selectedYears}
              onChange={handleYearChange}
              allowClear
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}more`}
            />
          }
        />
      </div>
      <Table
        columns={columns}
        dataSource={processedData.data}
        pagination={{
          current: tableParams.pagination.current,
          pageSize: tableParams.pagination.pageSize,
          total: processedData.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          pageSizeOptions: [15, 20, 50, 100],
        }}
        loading={{ spinning: loading, indicator: <Spin indicator={<LoadingOutlined />} /> }}
        onChange={handleTableChange}
        rowKey={(record, index) => `${record.year}-${index}`}
        size="small"
        className="border-top-table"
        scroll={
          (processedData.data?.length || 0) > 6
            ? { x: 1200, y: window.innerHeight - 256 }
            : undefined
        }
      />

      {dialog?.type === "addtaxrates" && (
        <AddTaxRates
          isViewOnly={isSubAdmin()}
          addedYears={addedYears}
          id={dialog.params?.year}
          record={dialog.params}
          onDismiss={(confirmation: any) => {
            setDialog({ type: "", params: {} });
            if (confirmation) {
              fetchData();
            }
          }}
        />
      )}
    </div>
  );
};

export default TaxRates;